<template>
  <div
    v-if="freelancer"
    class="lg:ml-5 p-fluid gap-4 max-h-full overflow-scroll w-full"
  >
    <div class="flex justify-content-between align-items-center mb-3">
      <Breadcrumb
        :home="home"
        :model="items"
      />

      <!-- Action Buttons -->
      <div class="flex gap-2">
        <Button
          v-if="!isEditing"
          label="Edit"
          icon="pi pi-pencil"
          class="p-button-primary"
          @click="startEdit"
        />
        <Button
          v-if="!isEditing && !isSure"
          label="Delete"
          icon="pi pi-trash"
          class="p-button-danger"
          @click="isSure = !isSure"
        />
        <Button
          v-if="isSure"
          label="Sure??"
          icon="pi pi-trash"
          class="p-button-danger"
          @click="deleteItem"
        />
      </div>
    </div>

    <!-- Main Layout: Image on Left, Details on Right -->
    <Fieldset
      legend="Overview"
      class="w-full mt-2 bg-surface"
    >
      <div class="flex flex-column md:flex-row align-items-start gap-4 mb-4">
        <!-- Contact Image with Rounded Edges -->
        <div class="flex flex-column align-items-center justify-content-center md:justify-content-start">
          <Image
            :src="freelancer.image || defaultImage"
            alt="Contact Image"
            width="350px"
            style="max-width: 100%; border-radius: var(--input-border-radius);"
          />
          <Button
            label="Upload"
            icon="pi pi-trash"
            class="p-button-danger"
            @click="onImageUpload"
          />
        </div>

        <!-- Contact Details Section -->
        <div class="grid formgrid flex-1">
          <!-- Name -->
          <div class="field col-12 md:col-6">
            <label for="name">Name</label>
            <InputText
              id="name"
              v-model="freelancer.name"
              :disabled="!isEditing"
              class="w-full"
            />
          </div>

          <!-- Rating -->
          <div class="field col-12 md:col-6">
            <label for="name">Rating</label>
            <Rating
              id="rating"
              v-model="freelancer.rating"
            />
          </div>
          <!-- Company -->
          <div class="field col-12 md:col-6">
            <label for="company">company</label>
            <InputText
              id="company"
              v-model="freelancer.company"
              :disabled="!isEditing"
              class="w-full"
            />
          </div>
          <!-- Website -->
          <div class="field col-12 md:col-6">
            <label for="website">Website</label>
            <InputText
              id="website"
              v-model="freelancer.website"
              :disabled="!isEditing"
              class="w-full"
            />
          </div>
          <!-- Description Field Full Width Below -->
          <div class="field col-12">
            <label for="description">Notes</label>
            <TextArea
              id="description"
              v-model="freelancer.notes"
              rows="3"
              :disabled="!isEditing"
              class="w-full"
            />
          </div>

          <!-- Bookable and VAT Checkboxes Next to Website -->
          <div class="field col-12 md:col-6 flex align-items-center gap-4 mt-4 md:mt-0">
            <div class="field-checkbox">
              <Checkbox
                v-model="freelancer.isBookable"
                :disabled="!isEditing"
                binary
              />
              <label for="bookable">Bookable</label>
            </div>
            <div class="field-checkbox">
              <Checkbox
                v-model="freelancer.isVat"
                :disabled="!isEditing"
                binary
              />
              <label for="vat">VAT</label>
            </div>
          </div>
        </div>
      </div>
    </Fieldset>

    <!-- Contact Details Fieldset -->
    <Fieldset
      legend="Contact Details"
      class="w-full mt-2 bg-surface"
    >
      <div class="field col-12 md:col-6">
        <label for="number">Phone Number</label>
        <InputText
          id="number"
          v-model="freelancer.number"
          :disabled="!isEditing"
          class="w-full"
        />
      </div>
      <div class="field col-12 md:col-12">
        <label for="email">Email</label>
        <InputText
          id="email"
          v-model="freelancer.email"
          :disabled="!isEditing"
          class="w-full"
        />
      </div>
      <div class="field col-12 md:col-12">
        <label for="website">Website</label>
        <InputText
          id="website"
          v-model="freelancer.website"
          :disabled="!isEditing"
          class="w-full"
        />
      </div>
    </Fieldset>
    <!-- Roles Table (Full Width Underneath) -->
    <Fieldset
      legend="Roles"
      class="w-full mt-2"
    >
      <!-- Flex Container for Button and Table -->
      <div class="flex flex-column h-full">
        <!-- Add Staff Button aligned to the right -->
        <div class="flex justify-content-end mb-2">
          <Button
            icon="pi pi-plus"
            severity="primary"
            rounded
            aria-label="Add"
          />
        </div>

        <!-- Scrollable DataTable -->
        <div class="flex-grow-1 overflow-auto">
          <DataTable
            :value="freelancer.roles"
            sort-field="name"
            scrollable
            scroll-height="flex"
            @row-click="onRowClick"
          >
            <template #empty>
              <p>No Roles have been found.</p>
            </template>

            <Column
              field="role"
              header="Role"
            />
            <Column
              field="rate"
              header="Rate"
              sortable
            />
            <Column
              field="rating"
              header="Rating"
              sortable
            >
              <template #body="slotProps">
                <Rating v-model="slotProps.data.rating" />
              </template>
            </Column>
            <Column>
              <template #body>
                <Button
                  icon="pi pi-times"
                  severity="danger"
                  rounded
                  outlined
                  aria-label="Delete"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </Fieldset>


    <!-- Skills Table (Full Width Underneath) -->
    <Fieldset
      legend="Skills"
      class="w-full mt-2"
    >
      <!-- Flex Container for Button and Table -->
      <div class="flex flex-column h-full">
        <!-- Add Skill Button aligned to the right -->
        <div class="flex justify-content-end mb-2">
          <Button
            icon="pi pi-plus"
            severity="primary"
            rounded
            aria-label="Add"
          />
        </div>

        <!-- Scrollable DataTable -->
        <div class="flex-grow-1 overflow-auto">
          <DataTable
            :value="freelancer.skills"
            sort-field="name"
            scrollable
            scroll-height="flex"
            @row-click="onRowClick"
          >
            <template #empty>
              <p>No Skills have been found.</p>
            </template>
            <Column field="hasDownload">
              <template #body="slotProps">
                <i
                  v-if="slotProps.data.hasDownload"
                  class="pi pi-download"
                />
              </template>
            </Column>

            <Column
              field="skill"
              header="Skill"
            />

            <Column
              field="expiry"
              header="Expiry"
              sortable
            />
            <Column
              field="rating"
              header="Rating"
              sortable
            >
              <template #body="slotProps">
                <Rating v-model="slotProps.data.rating" />
              </template>
            </Column>
            <Column>
              <template #body>
                <Button
                  icon="pi pi-times"
                  severity="danger"
                  rounded
                  outlined
                  aria-label="Delete"
                />
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </Fieldset>

    <!-- Bookings Table (Full Width Underneath) -->
    <Fieldset
      legend="Bookings"
      class="w-full mt-2"
    >
      <!-- Flex Container for Button and Table -->
      <div class="flex flex-column h-full">
        <!-- Scrollable DataTable -->
        <div class="flex-grow-1 overflow-auto">
          <DataTable
            :value="freelancer.projects"
            sort-field="name"
            scrollable
            scroll-height="flex"
            @row-click="onRowClick"
          >
            <template #empty>
              <p>No Bookings have been found.</p>
            </template>

            <Column
              field="projectNumber"
              header="#"
            />
            <Column
              field="name"
              header="Project Name"
              sortable
            />
            <Column
              field="start"
              header="Start"
              sortable
            />
            <Column
              field="end"
              header="End"
              sortable
            />
            <Column
              field="projectManager"
              header="Project Manager"
              sortable
            />
          </DataTable>
        </div>
      </div>
    </Fieldset>
  </div>



  <!-- Save and Cancel Buttons for Editing -->
  <div
    v-if="isEditing"
    class="flex justify-content-center gap-3 mt-4"
  >
    <Button
      label="Update"
      icon="pi pi-check"
      class="p-button-primary"
      @click="updateItem"
    />
    <Button
      label="Cancel"
      icon="pi pi-times"
      class="p-button-secondary"
      @click="cancelEdit"
    />
  </div>
</template>



<script setup>
import { onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useFreelanceStore } from '@/stores/freelanceStaffStore';
import defaultImage from '@/assets/icons/Profile.svg';
import { useEditableDetail } from '@/composables/useEditableDetail';

const freelanceStore = useFreelanceStore();
const route = useRoute();
const isSure = ref(false)



const {
    item: freelancer,
    isEditing,
    loadItem,
    startEdit,
    cancelEdit,
    updateItem,
    deleteItem,
    onImageUpload,
} = useEditableDetail(
    {
        getItemById: freelanceStore.getFreelancerById,
        updateItem: freelanceStore.updateFreelancer,
        deleteItem: freelanceStore.deleteFreelancer,
    },
    {
        redirectAfterDelete: '/freelance-staff',
        defaultImage,
    }
);


onMounted(() => {
    const freelancerId = parseInt(route.params.id);
    loadItem(freelancerId); // Load the freelancer using the composable
});


// Breadcrumb configuration
const home = ref({ icon: 'pi pi-home', url: '/office' });
const items = ref([
    { label: 'Freelancers', url: '/freelance-staff' },
    { label: 'View Freelancer' }
]);
</script>
<style scoped>
.image-rounded {
    border-radius: var(--input-border-radius);
}
</style>
